import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [mobileYatrasOpen, setMobileYatrasOpen] = useState(false);
    const location = useLocation();
    
    // Check if we are on the Home page (where the cinematic hero is)
    const isHome = location.pathname === "/";

    // Dynamic Navbar Effect on Scroll - Trigger after Hero is gone
    useEffect(() => {
        const handleScroll = () => {
            // On Home: Switch once user has scrolled past the hero section
            // On Other Pages: Always solid (controlled by the render logic below)
            if (window.scrollY > window.innerHeight - 80) {
                setScrolled(true);
            } else {
                setScrolled(false);
            }
        };

        window.addEventListener("scroll", handleScroll);
        // Initial check
        handleScroll();
        
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Final logic: If NOT on home, it's ALWAYS "scrolled" (solid)
    // If on Home, it depends on scroll state
    const shouldBeSolid = !isHome || scrolled;

    const navItems = [
        { name: "Home", path: "/" },
        { name: "Explore", path: "/explore" },
        { name: "About", path: "/about" },
        { name: "Blogs", path: "/blogs" }
    ];

    const yatraItems = [
        { name: "Char Dham Yatra", path: "/char-dham-yatra" },
        { name: "Amarnath Yatra", path: "/amarnath-yatra" },
        { name: "Pancha Bhoota Sthalam", path: "/pancha-bhoota-sthalam" },
        { name: "Arupadai Veedu", path: "/arupadai-veedu" },
        { name: "Karnataka Mukti Kshetras", path: "/mukti-kshetras-karnataka" }
    ];

    const handlePrefetch = (item) => {
        if (item === "Explore") import("../pages/Explore");
        else if (item === "About") import("../pages/About");
        else if (item === "Blogs") import("../pages/Blogs");
        else if (item === "Char Dham Yatra") import("../pages/CharDhamYatra");
        else if (item === "Amarnath Yatra") import("../pages/AmarnathYatra");
        else if (item === "Pancha Bhoota Sthalam") import("../pages/PanchaBhootaSthalam");
        else if (item === "Arupadai Veedu") import("../pages/ArupadaiVeedu");
        else if (item === "Karnataka Mukti Kshetras") import("../pages/MuktiKshetrasKarnataka");
    };

    return (
        <nav className={`fixed top-0 left-0 right-0 z-[100] transition-all duration-[400ms] ease-out px-6 md:px-8 py-4 ${
            shouldBeSolid 
            ? "bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-100 py-3" 
            : "bg-transparent py-6"
        }`}>
            <div className="max-w-[1400px] mx-auto flex items-center justify-between">
                {/* Logo Section */}
                <Link 
                    to="/" 
                    className="flex items-center gap-2 group"
                    onMouseEnter={() => import("../pages/Home")}
                    onTouchStart={() => import("../pages/Home")}
                >
                    <img 
                        src="/logo.svg" 
                        alt="Akhandaa Logo" 
                        className="h-7 md:h-9 w-auto object-contain transition-transform group-hover:rotate-12" 
                    />
                    <img 
                        src="/Akhandaa.svg" 
                        alt="Akhandaa" 
                        className="h-5 md:h-6.5 w-auto object-contain transition-all duration-300" 
                    />
                </Link>

                <div className="hidden md:flex items-center gap-8">
                    {navItems.map((item) => (
                        <Link 
                            key={item.name}
                            to={item.path} 
                            onMouseEnter={() => handlePrefetch(item.name)}
                            onTouchStart={() => handlePrefetch(item.name)}
                            className={`text-xs font-black uppercase tracking-[0.2em] transition-all duration-300 ${
                                shouldBeSolid 
                                ? "text-slate-600 hover:text-sky-600" 
                                : "text-white/90 hover:text-white drop-shadow-sm"
                            }`}
                        >
                            {item.name}
                        </Link>
                    ))}

                    {/* Yatras Hover Dropdown */}
                    <div className="relative group py-2">
                        <button 
                            className={`text-xs font-black uppercase tracking-[0.2em] transition-all duration-300 flex items-center gap-1.5 focus:outline-none ${
                                shouldBeSolid 
                                ? "text-slate-600 hover:text-sky-600" 
                                : "text-white/90 hover:text-white drop-shadow-sm"
                            }`}
                        >
                            Yatras
                            <svg className="w-3 h-3 transition-transform duration-300 group-hover:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        
                        {/* Dropdown Menu */}
                        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-72 bg-white/95 backdrop-blur-md border border-slate-100 rounded-3xl p-3 shadow-2xl flex flex-col gap-1 transition-all duration-300 opacity-0 invisible translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0">
                            {yatraItems.map((item) => (
                                <Link
                                    key={item.name}
                                    to={item.path}
                                    onMouseEnter={() => handlePrefetch(item.name)}
                                    onTouchStart={() => handlePrefetch(item.name)}
                                    className="px-5 py-3.5 rounded-2xl font-black text-[10px] md:text-xs uppercase tracking-widest text-slate-600 hover:bg-sky-50 hover:text-sky-600 transition-all flex items-center justify-between group/link"
                                >
                                    {item.name}
                                    <svg className="w-3.5 h-3.5 opacity-0 -translate-x-1 group-hover/link:opacity-100 group-hover/link:translate-x-0 transition-all duration-300 text-sky-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
                                    </svg>
                                </Link>
                            ))}
                        </div>
                    </div>

                    {/* Instagram Button */}
                    <a
                        href="https://www.instagram.com/akhandaa_official?igsi=MXRibG9mNjVkdW5ncg=="
                        target="_blank"
                        rel="noopener noreferrer"
                        className="transition-transform duration-300 hover:scale-110 flex items-center justify-center p-1"
                        title="Follow Akhandaa on Instagram"
                    >
                        <div className="w-7 h-7 rounded-full bg-gradient-to-tr from-amber-400 via-rose-500 to-purple-600 text-white flex items-center justify-center shrink-0 shadow-sm">
                            <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                            </svg>
                        </div>
                    </a>
                </div>

                {/* Mobile Menu Button */}
                <button 
                    onClick={() => setIsOpen(!isOpen)}
                    className={`md:hidden p-2 rounded-lg transition-colors relative z-[130] ${
                        isOpen
                        ? "text-slate-900 hover:bg-slate-100"
                        : (shouldBeSolid ? "text-slate-900 hover:bg-slate-100" : "text-white hover:bg-white/10")
                    }`}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d={isOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
                    </svg>
                </button>
            </div>

            {/* Mobile Sidebar Overlay */}
            <div className={`fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[90] md:hidden transition-opacity duration-300 ${isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`} onClick={() => setIsOpen(false)}></div>
            
            {/* Mobile Sidebar */}
            <div className={`fixed top-0 right-0 h-full w-[280px] bg-white z-[120] md:hidden transform transition-transform duration-300 ease-in-out shadow-2xl overflow-y-auto ${isOpen ? "translate-x-0" : "translate-x-full"}`}>
                <div className="p-8 flex flex-col gap-6 pt-24">
                    {navItems.map((item) => (
                        <Link 
                            key={item.name}
                            to={item.path} 
                            onMouseEnter={() => handlePrefetch(item.name)}
                            onTouchStart={() => handlePrefetch(item.name)}
                            onClick={() => setIsOpen(false)} 
                            className="text-xl font-black text-slate-900 uppercase tracking-widest border-b border-slate-50 pb-3"
                        >
                            {item.name}
                        </Link>
                    ))}

                    {/* Mobile Yatras Dropdown */}
                    <div className="flex flex-col gap-3">
                        <button 
                            onClick={() => setMobileYatrasOpen(!mobileYatrasOpen)}
                            className="text-xl font-black text-slate-900 uppercase tracking-widest border-b border-slate-50 pb-3 flex justify-between items-center w-full focus:outline-none"
                        >
                            <span>Yatras</span>
                            <svg className={`w-5 h-5 transition-transform duration-300 ${mobileYatrasOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 9l-7 7-7-7"/>
                            </svg>
                        </button>
                        {mobileYatrasOpen && (
                            <div className="flex flex-col gap-4 pl-4 pt-1 pb-2">
                                {yatraItems.map((item) => (
                                    <Link 
                                        key={item.name}
                                        to={item.path}
                                        onMouseEnter={() => handlePrefetch(item.name)}
                                        onTouchStart={() => handlePrefetch(item.name)}
                                        onClick={() => { setIsOpen(false); setMobileYatrasOpen(false); }}
                                        className="text-sm font-bold text-slate-650 hover:text-sky-600 uppercase tracking-widest transition-colors"
                                    >
                                        {item.name}
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Mobile Instagram Button */}
                    <div className="pt-4 border-t border-slate-100 mt-2">
                        <a
                            href="https://www.instagram.com/akhandaa_official?igsi=MXRibG9mNjVkdW5ncg=="
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-3 text-slate-900 font-bold hover:text-pink-600 transition-colors"
                        >
                            <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-amber-500 via-rose-500 to-purple-600 text-white flex items-center justify-center shadow-md">
                                <svg className="w-4.5 h-4.5 fill-current" viewBox="0 0 24 24">
                                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                                </svg>
                            </div>
                            <span className="text-sm font-bold uppercase tracking-wider">Instagram</span>
                        </a>
                    </div>
                </div>
            </div>
        </nav>
    );
};

export default Navbar;
